/**
 * Live Quick-Order Execution Sizing & Parameter Resolution.
 *
 * Resolves contract lot multiples, market protection limit prices,
 * and stop-loss trigger conditions for single-click order entry.
 */

export interface ContractDetails {
  tsym: string;
  exch: string;
  ls: number; // lot size
  ti?: number; // tick size
  [key: string]: any;
}

export interface QuickOrderInputParams {
  contract: ContractDetails | null | undefined;
  optType: string;
  lots: number;
  orderType: string;
  limitPriceInput?: number | string | null;
  triggerPriceInput?: number | string | null;
  ltp?: number | null;
  protectionBand: number;
  side: "BUY" | "SELL";
}

export interface QuickOrderResolveResult {
  valid: boolean;
  error?: string;
  qty?: number;
  price?: number;
  trigger?: number | null;
  brokerOrderType?: string;
}

/**
 * Calculates total quick order quantity given lot count and contract lot size.
 */
export function calculateQuickOrderQty(lots: number, lotSize: number): number {
  const nLots = parseInt(String(lots || 0), 10);
  const nLotSize = parseInt(String(lotSize || 1), 10);
  return (
    Math.max(0, isNaN(nLots) ? 0 : nLots) *
    Math.max(1, isNaN(nLotSize) ? 1 : nLotSize)
  );
}

/**
 * Validates quick-order inputs and calculates execution limit/trigger prices.
 */
export function resolveQuickOrderParams(
  params: QuickOrderInputParams,
): QuickOrderResolveResult {
  const {
    contract,
    optType,
    lots,
    orderType,
    limitPriceInput,
    triggerPriceInput,
    ltp,
    protectionBand,
    side,
  } = params;

  if (!contract) {
    return {
      valid: false,
      error: `${optType} contract not resolved — order NOT placed`,
    };
  }

  const qty = calculateQuickOrderQty(lots, contract.ls);
  if (qty <= 0) {
    return { valid: false, error: "Bad quantity" };
  }

  let price: number | null = null;
  if (orderType === "MARKET") {
    if (!ltp || ltp <= 0) {
      return {
        valid: false,
        error: "No LTP for protection price — order NOT placed",
      };
    }
    const b = protectionBand || 0;
    price = side === "BUY" ? ltp * (1 + b) : ltp * (1 - b);
  } else {
    const p =
      typeof limitPriceInput === "number"
        ? limitPriceInput
        : parseFloat(String(limitPriceInput || 0));
    if (!p || isNaN(p) || p <= 0) {
      return { valid: false, error: "A limit price is required" };
    }
    price = p;
  }

  let trigger: number | null = null;
  if (orderType === "SL") {
    const trg =
      typeof triggerPriceInput === "number"
        ? triggerPriceInput
        : parseFloat(String(triggerPriceInput || 0));
    if (!trg || isNaN(trg) || trg <= 0) {
      return { valid: false, error: "SL needs a trigger price" };
    }
    trigger = trg;
  }

  const brokerOrderType = orderType === "SL" ? "SL-LMT" : "LMT";

  return {
    valid: true,
    qty,
    price,
    trigger,
    brokerOrderType,
  };
}
